import { ReactElement, useMemo } from 'react';
import { List, RowComponentProps } from 'react-window';

import './VirtualizedGroupedList.scss';

const GROUP_HEADER_HEIGHT = 40;

type Row<T> = { kind: 'group'; label: string; count: number } | { kind: 'item'; item: T };

interface RowRendererProps<T> {
  rows: Row<T>[];
  renderItem: (item: T) => ReactElement;
  groupLabelClassName: string;
  groupCountClassName: string;
}

const RowRenderer = <T,>({
  index,
  style,
  rows,
  renderItem,
  groupLabelClassName,
  groupCountClassName
}: RowComponentProps<RowRendererProps<T>>) => {
  const row = rows[index];
  if (row.kind === 'group') {
    return (
      <div style={style} className={groupLabelClassName}>
        <span>{row.label}</span>
        <span className={groupCountClassName}>{row.count}</span>
      </div>
    );
  }
  return <div style={style}>{renderItem(row.item)}</div>;
};

export interface VirtualizedGroupedListProps<T> {
  items: T[];
  groupOf: (item: T) => string;
  keyOf: (item: T) => string;
  itemHeight: number;
  renderItem: (item: T) => ReactElement;
  groupLabelClassName: string;
  groupCountClassName: string;
  emptyClassName: string;
  emptyMessage?: string;
}

// Flattens items into interleaved group-header/item rows and virtualizes them with react-window.
// Shared by StreamingNetworks and LinearNetworks, whose master lists only differ in item shape,
// grouping key, row height, and per-page class names — all passed in by the caller.
export const VirtualizedGroupedList = <T,>({
  items,
  groupOf,
  keyOf,
  itemHeight,
  renderItem,
  groupLabelClassName,
  groupCountClassName,
  emptyClassName,
  emptyMessage = 'No results match.'
}: VirtualizedGroupedListProps<T>): ReactElement => {
  // groupOf/keyOf are expected to be stable (module-level) functions, so this only
  // recomputes when the underlying item list actually changes, not on every render.
  const rows = useMemo<Row<T>[]>(() => {
    const byGroup = new Map<string, T[]>();
    items.forEach(item => {
      const group = groupOf(item);
      const list = byGroup.get(group) ?? [];
      list.push(item);
      byGroup.set(group, list);
    });
    const flat: Row<T>[] = [];
    byGroup.forEach((groupItems, group) => {
      flat.push({ kind: 'group', label: group, count: groupItems.length });
      groupItems.forEach(item => flat.push({ kind: 'item', item }));
    });
    return flat;
  }, [items, groupOf]);

  if (rows.length === 0) return <div className={emptyClassName}>{emptyMessage}</div>;

  return (
    <List
      className='vgl-list'
      defaultHeight={480}
      rowCount={rows.length}
      rowHeight={(index, props) =>
        props.rows[index].kind === 'group' ? GROUP_HEADER_HEIGHT : itemHeight
      }
      rowComponent={RowRenderer}
      rowProps={{ rows, renderItem, groupLabelClassName, groupCountClassName }}
      rowKey={(index, props) => {
        const row = props.rows[index];
        return row.kind === 'group' ? `group-${row.label}` : keyOf(row.item);
      }}
    />
  );
};
